import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { useT } from "../i18n";
import { useShell } from "../ui";

interface Q { id: string; q: string; options: string[] }
interface Answer { right: boolean; correctAnswer: string; streak: number; ended: boolean; perk?: { id: string; kind: string; code: string } }
const STEPS = ["Checking flights…", "Comparing hotel rates…", "Adding transfers…", "Applying partner perks…", "Building your quote…"];

/**
 * The Departure Lounge (docs/37 §2.3, S7): ten questions from the server's bank, answered one by one; the server says
 * right or wrong and gives the perk at three in a row. Opened from the promo, or while the builder searches.
 */
export function Lounge({ onClose, onDone }: { onClose: () => void; onDone?: (() => void) | undefined }) {
  const t = useT();
  const { toast, reload } = useShell();
  const [session, setSession] = useState<{ sessionId: string; questions: Q[] } | null>(null);
  const [i, setI] = useState(0);
  const [streak, setStreak] = useState(0);
  const [picked, setPicked] = useState<{ option: string; answer: Answer } | null>(null);
  const [won, setWon] = useState<Answer["perk"]>();
  const [ended, setEnded] = useState(false);
  const [pct, setPct] = useState(0);
  const [err, setErr] = useState<string>();
  const timer = useRef<ReturnType<typeof setInterval>>(undefined);

  const start = () => { setI(0); setStreak(0); setPicked(null); setWon(undefined); setEnded(false); api<{ sessionId: string; questions: Q[] }>("/lounge/start", { body: {} }).then(setSession, (e: Error) => setErr(e.message)); };
  useEffect(() => {
    start();
    if (onDone) timer.current = setInterval(() => setPct((p) => Math.min(100, p + 1.25)), 150);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q = session?.questions[i];
  const answer = async (option: string) => {
    if (!session || !q || picked) return;
    try {
      const a = await api<Answer>(`/lounge/${session.sessionId}/answers`, { body: { questionId: q.id, answer: option } });
      setPicked({ option, answer: a });
      setStreak(a.streak);
      if (a.perk) { setWon(a.perk); toast(`${t("lounge.won")}: ${t(`perk.${a.perk.kind}`)}`); void reload(); }
      setTimeout(() => { setPicked(null); if (a.ended) setEnded(true); else setI((n) => n + 1); }, a.right ? 650 : 1300);
    } catch (e) { setErr((e as Error).message); }
  };

  return (
    <div className="overlay">
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="lg-title">
        <div className="dlg-top">
          <div className="row"><b id="lg-title">{onDone ? t("lounge.searching") : t("lounge.title")}</b><button className="x" type="button" aria-label={t("common.close")} onClick={() => { onClose(); if (onDone) onDone(); }}>×</button></div>
          {onDone ? <div><div className="load"><i style={{ width: `${pct}%` }} /></div><div className="load-txt" style={{ marginTop: ".5rem" }}><span>{STEPS[Math.min(STEPS.length - 1, Math.floor(pct / 20))]}</span><span>{Math.round(pct)}%</span></div></div> : null}
        </div>
        <div className="game">
          <div className="gh"><span className="eyebrow">{t("lounge.streak")}</span><div className="streak" data-testid="streak">{[0, 1, 2].map((k) => <i key={k} className={k < streak ? "on" : ""} />)}</div></div>
          {err ? <p className="muted">{err}</p> : null}
          {ended || !q ? (
            <div className="win">{won ? <><span className="eyebrow">{t("lounge.won")}</span><b>{t(`perk.${won.kind}`)}</b><span className="muted">Saved to your Perks wallet. Code {won.code}</span></> : session ? <span className="muted">That was the last question.</span> : null}<button className="btn line" type="button" onClick={start} style={{ marginTop: ".6rem" }}>{t("lounge.again")}</button></div>
          ) : (
            <>
              <div className="q" data-testid="lounge-q">{q.q}</div>
              <div className="opts">{q.options.map((o) => <button key={o} type="button" data-option={o} className={picked ? (o === picked.answer.correctAnswer ? "right" : o === picked.option ? "wrong" : "") : ""} onClick={() => void answer(o)}>{o}</button>)}</div>
              {won ? <div className="win"><span className="eyebrow">{t("lounge.won")}</span><b>{t(`perk.${won.kind}`)}</b><span className="muted">Code {won.code}</span></div> : null}
            </>
          )}
        </div>
        {onDone && pct >= 100 ? <div className="ready"><span><b>{t("lounge.ready")}</b></span><button className="btn ink" type="button" onClick={() => { onClose(); onDone(); }}>{t("lounge.see")}</button></div> : null}
      </div>
    </div>
  );
}
